import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authClient, storageKey } from '../authClient';
import { takeReturnPath } from '../helpers/returnPath';

// -------------------
// Define types
// -------------------

interface UserProfile {
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  roles?: string[];
  [key: string]: any;
}

interface UserInfo {
  fullName: string;
  email: string;
  roles: string[];
  claims: UserProfile;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserProfile | null;
  userInfo: UserInfo | null;
  hasBtCommunicationRole: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

interface AuthState {
  isAuthenticated: boolean;
  user: UserProfile | null;
  userInfo: UserInfo | null;
  hasBtCommunicationRole: boolean;
}

const MS_ROLE_CLAIM =
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
const ZITADEL_ROLE_CLAIM = 'urn:zitadel:iam:org:project:roles';
const REQUIRED_ROLE = 'BtCommunicationApp';

const signedOutState: AuthState = {
  isAuthenticated: false,
  user: null,
  userInfo: null,
  hasBtCommunicationRole: false,
};

const extractRoles = (profile: UserProfile): string[] => {
  const msRoles = profile[MS_ROLE_CLAIM];
  if (Array.isArray(msRoles)) return msRoles;
  if (typeof msRoles === 'string') return [msRoles];

  const zitadelRoles = profile[ZITADEL_ROLE_CLAIM];
  if (zitadelRoles && typeof zitadelRoles === 'object') {
    return Object.keys(zitadelRoles);
  }

  return Array.isArray(profile.roles) ? profile.roles : [];
};

const readAuthState = (): AuthState => {
  const stored = window.localStorage.getItem(storageKey);
  if (!stored) return signedOutState;

  try {
    const parsed = JSON.parse(stored);
    const expiresAt: number | undefined = parsed.expires_at;
    if (typeof expiresAt === 'number' && expiresAt * 1000 <= Date.now()) {
      return signedOutState;
    }

    const profile: UserProfile = parsed.profile || {};
    const roles = extractRoles(profile);

    return {
      isAuthenticated: true,
      user: profile,
      userInfo: {
        fullName:
          profile.name ||
          `${profile.given_name || ''} ${profile.family_name || ''}`.trim(),
        email: profile.email || '',
        roles,
        claims: profile,
      },
      hasBtCommunicationRole: roles.includes(REQUIRED_ROLE),
    };
  } catch (error) {
    console.error('Error parsing user info:', error);
    return signedOutState;
  }
};

// -------------------
// Create context
// -------------------

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// -------------------
// AuthProvider
// -------------------

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Auth state is read synchronously from storage, so there is no loading phase.
  const [authState, setAuthState] = useState<AuthState>(readAuthState);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const syncAuthState = () => setAuthState(readAuthState());

    syncAuthState();
    window.addEventListener('storage', syncAuthState);

    const { userManager } = authClient;
    userManager.events.addUserLoaded(syncAuthState);
    userManager.events.addUserUnloaded(syncAuthState);
    userManager.events.addAccessTokenExpired(syncAuthState);

    return () => {
      window.removeEventListener('storage', syncAuthState);
      userManager.events.removeUserLoaded(syncAuthState);
      userManager.events.removeUserUnloaded(syncAuthState);
      userManager.events.removeAccessTokenExpired(syncAuthState);
    };
  }, []);

  useEffect(() => {
    if (!authState.isAuthenticated) return;

    if (authState.hasBtCommunicationRole) {
      // LoginCallback redirects itself once the sign-in completes.
      if (location.pathname === '/login') {
        navigate(takeReturnPath(), { replace: true });
      }
    } else if (location.pathname !== '/not-authorized') {
      navigate('/not-authorized');
    }
  }, [authState, location.pathname, navigate]);

  const login = useCallback(async (): Promise<void> => {
    try {
      await authClient.authorize();
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authClient.signout();
      setAuthState(signedOutState);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ ...authState, isLoading: false, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// -------------------
// useAuth hook
// -------------------

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
